import { useContext, useCallback, useMemo } from 'react'
import {
  ShareVersionContext,
  VersioningContext,
  VersioningContextReady,
  ShareWithoutVersion,
} from './ShareVersionContext'
import { VersionFragment } from '@sketch/gql-types'
import {
  getVersionedRoute,
  VersionedRoutes,
  RouteParam,
  routes,
} from '@sketch/modules-common'

interface GetVersionPathnameProps<Key extends keyof VersionedRoutes> {
  versionShortId: string | null
  isViewingLatestVersion: boolean
  routeKey: Key
  routeParams: RouteParam<Key>
}

export interface GetPathnameProps<Key extends keyof VersionedRoutes>
  extends Pick<GetVersionPathnameProps<Key>, 'routeKey' | 'routeParams'> {}

// Special interface for PREVIEW_VIEW route
export interface GetPreviewPathnameProps {
  routeKey: 'PREVIEW_VIEW'
  routeParams: { previewID: string }
}

// Combined type for all supported routes
export type GetPathnamePropsAll<Key extends keyof VersionedRoutes> =
  | GetPathnameProps<Key>
  | GetPreviewPathnameProps

const getVersionedPathname = <Key extends keyof VersionedRoutes>({
  versionShortId,
  isViewingLatestVersion,
  routeKey,
  routeParams: params,
}: GetVersionPathnameProps<Key>) => {
  const route = getVersionedRoute(routeKey, isViewingLatestVersion)

  const routeParams =
    isViewingLatestVersion || !versionShortId
      ? params || {}
      : { ...(params || {}), versionShortId }

  return route.create(routeParams)
}

const isVersionContextReady = (
  value: VersioningContext
): value is VersioningContextReady => !value.loading

export const useVersioning = () => {
  const context = useContext(ShareVersionContext)!

  // Call all hooks unconditionally at the top level
  const getPathnamePreview = useCallback(
    (props: GetPreviewPathnameProps | GetPathnameProps<any>) => {
      if (props.routeKey === 'PREVIEW_VIEW') {
        return routes.PREVIEW_VIEW.create(
          props.routeParams as { previewID: string }
        )
      }
      if (props.routeKey === 'PREVIEW_PAGE_VIEW') {
        return routes.PREVIEW_VIEW.create(
          props.routeParams as { previewID: string; pageUUID: string }
        )
      }
      if (props.routeKey === 'PREVIEW_PROTOTYPES') {
        return routes.PREVIEW_PROTOTYPES.create(
          props.routeParams as { previewID: string }
        )
      }
      // For versioned routes without context, we can't generate meaningful paths
      return '/'
    },
    []
  )

  // Extract data for conditional processing
  const { isViewingLatestVersion, versionShortId, share, latestVersion } =
    context || {}

  const getPathname = useCallback(
    <Key extends keyof VersionedRoutes>(props: GetPathnameProps<Key>) =>
      context
        ? getVersionedPathname({
            ...props,
            isViewingLatestVersion,
            versionShortId,
          })
        : '/',
    [context, isViewingLatestVersion, versionShortId]
  )

  const versionIdentifier = useMemo(
    () =>
      context && isVersionContextReady(context)
        ? context.currentVersion.identifier
        : undefined,
    /*
      Since react uses the `Object.is` algorithm, object dependencies would need to
      refer to the same object in memory. Using `currentVersion.identifier` instead.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(context as VersioningContextReady)?.currentVersion?.identifier]
  )

  if (!context) {
    // For preview views without version context

    // Create minimal valid objects for preview context
    const mockVersion: VersionFragment = {
      __typename: 'Version',
      identifier: '',
      shortId: '',
      kind: 'DRAFT',
      createdAt: '',
      updatedAt: '',
      description: null,
      userIsCreator: false,
      createdAfterVersioning: false,
      document: null,
      starredAt: null,
      isStarred: false,
      hasComponentManifest: false,
      presentationStatus: 'UNKNOWN' as any,
      creator: null,
    } as VersionFragment

    const mockShare: ShareWithoutVersion = {
      __typename: 'Share',
      identifier: '',
      name: 'Preview',
      publicAccessLevel: 'NONE' as any,
      userCanEdit: false,
      userCanInspect: false,
      userCanUpdateSettings: false,
      shareType: 'CLOUD_DOCUMENT' as any,
      commentsEnabled: false,
      subscriptionStatus: 'OFF' as any,
      userAccessLevel: 'VIEW' as any,
      project: null,
      workspace: {
        __typename: 'PublicWorkspace',
        identifier: '',
        name: '',
        userIsMember: false,
        userRole: null,
        userIsOwner: false,
        type: 'REGULAR' as any,
        customer: null,
        avatar: null,
      } as any,
      collection: null,
      pinnedByCurrentUserAt: null,
    } as ShareWithoutVersion

    // so the useVersioning context typing isn't out of whack when used out of the preview things
    return {
      loading: false,
      share: mockShare,
      latestVersion: mockVersion,
      currentVersion: mockVersion,
      isViewingLatestVersion: true,
      hasPendingPatches: false,
      latestVersionId: '',
      versionShortId: '',
      versionIdentifier,
      getPathname: getPathnamePreview,
      isLibrary: false,
      onVersionUpdateNeeded: async () => {},
      setArtboardThumbnail: () => {},
      artboardThumbnail: '',
      renderStatus: null,
    }
  }

  // Context exists, use the normal return path
  return {
    ...context,
    versionIdentifier,
    getPathname,
    isLibrary: share.shareType === 'LIBRARY',
    latestVersion,
  }
}
